<template>
  <div>

    <button
        @click="toggleDebugPanel"
        :class="[
        'fixed z-[200] bg-blue-500 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition-all duration-300 ease-in-out',
        buttonPositionClass
      ]"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>

    <div
        ref="debugPanelRef"
        :class="[
        'fixed z-[500] bg-gray-800 text-white p-4 text-sm font-mono transition-all duration-300 ease-in-out',
        { 'w-full h-64': ['top', 'bottom'].includes(currentPosition) },
        { 'h-full': ['left', 'right'].includes(currentPosition) }
      ]"
        :style="panelStyle"
    >
      <div class="h-full flex flex-col">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-bold">Debug Panel</h2>
          <div class="flex items-center">
            <select
                v-model="currentPosition"
                class="bg-gray-700 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 mr-2"
            >
              <option value="bottom">Bottom</option>
              <option value="top">Top</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
            <select
                v-model="opacity"
                class="bg-gray-700 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 mr-2"
            >
              <option :value="1">100%</option>
              <option :value="0.9">90%</option>
              <option :value="0.8">80%</option>
              <option :value="0.7">70%</option>
              <option :value="0.6">60%</option>
              <option :value="0.5">50%</option>
            </select>
            <select
                v-if="['left', 'right'].includes(currentPosition)"
                v-model="selectedWidth"
                @change="updatePanelWidth"
                class="bg-gray-700 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 mr-2"
            >
              <option value="30">30%</option>
              <option value="50">50%</option>
              <option value="60">60%</option>
              <option value="75">75%</option>
            </select>
            <button @click="toggleDebugPanel" class="text-gray-400 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="flex-grow overflow-y-auto">
          <table class="w-full mb-4 border-collapse">
            <thead>
            <tr>
              <th class="text-left font-bold border border-gray-600 bg-gray-700 p-2" colspan="2">System Info</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td class="border border-gray-600 p-2">Version:</td>
              <td class="border border-gray-600 p-2">{{ config.public.version }}</td>
            </tr>
            <tr>
              <td class="border border-gray-600 p-2">Memory Usage:</td>
              <td class="border border-gray-600 p-2" v-html="memoryUsage"></td>
            </tr>
            <tr>
              <td class="border border-gray-600 p-2">Cookie Status:</td>
              <td class="border border-gray-600 p-2">{{ cookieStatus }}</td>
            </tr>
            </tbody>
          </table>


          <div class="flex">

            <div v-for="(according, index) in accordingList" :key="index" class="flex items-center me-4">
              <input :id="'show-table-panel-'+index" type="checkbox" :value="according"
                     v-model="showTablePanels"
                     class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600">
              <label :for="'inline-checkbox-'+index" class="ms-2 text-sm font-medium text-white">{{ according }}</label>
            </div>

          </div>

          <div class="mt-5" v-for="(according, index) in accordingList" :key="index">
          <table v-if="showTablePanels.includes(according)"  class="w-full mb-2 border-collapse">
            <thead>
            <tr>
              <th class="text-left font-bold border border-gray-600
              hover:bg-blue-700
              bg-gray-700 p-2 cursor-pointer"
                  colspan="2">
                {{according}}
              </th>
            </tr>
            </thead>
            <template v-if="according === 'Cookie Values'">
            <DebugCookies  :cookies="cookies" />
            </template>
            <template v-if="according === 'Nuxt Config'">
              <tr>
                <td class="border border-gray-600 bg-blue-700 p-2" colspan="2">Client Side Only</td>
              </tr>
              <DebugNuxtConfig   />
            </template>

          </table>

          </div>
        </div>
      </div>
    </div>

    <div
        v-if="['left', 'right'].includes(currentPosition)"
        ref="resizeHandleRef"
        @mousedown="startResize"
        :class="[
        'fixed top-0 bottom-0 w-1 bg-blue-500 opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-ew-resize',
        { 'left-0': currentPosition === 'left' },
        { 'right-0': currentPosition === 'right' }
      ]"
    ></div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import DebugCookies from "~/components/debug/DebugCookies.vue";
import DebugNuxtConfig from "~/components/debug/DebugNuxtConfig.vue";

const config = useRuntimeConfig();

const debugPanelRef = ref(null);
const resizeHandleRef = ref(null);
const isOpen = ref(false);
const currentPosition = ref('right');
const opacity = ref(1);
const memoryUsage = ref('Loading...');
const cookieStatus = ref('Loading...');
const cookies = ref([]);
const panelWidth = ref(550);
const selectedWidth = ref(50);

const selectedAccording = ref('Cookie Values');

const accordingList = ref([
  'Nuxt Config',
  'Cookie Values',
]);


const showTablePanels = ref([
  'Nuxt Config'
]);



const panelStyle = computed(() => {
  const style = {
    opacity: opacity.value,
    transform: isOpen.value ? 'translate(0, 0)' : getTransform(),
  };

  if (['left', 'right'].includes(currentPosition.value)) {
    style.width = `${panelWidth.value}px`;
  }

  switch (currentPosition.value) {
    case 'top':
      style.top = '0';
      style.left = '0';
      style.right = '0';
      break;
    case 'bottom':
      style.bottom = '0';
      style.left = '0';
      style.right = '0';
      break;
    case 'left':
      style.top = '0';
      style.left = '0';
      style.bottom = '0';
      break;
    case 'right':
      style.top = '0';
      style.right = '0';
      style.bottom = '0';
      break;
  }

  return style;
});

const buttonPositionClass = computed(() => {
  switch (currentPosition.value) {
    case 'top': return isOpen.value ? 'bottom-4 right-4' : 'top-4 right-4';
    case 'bottom': return 'bottom-4 right-4';
    case 'left': return isOpen.value ? 'bottom-4 right-4' : 'bottom-4 left-4';
    case 'right': return 'bottom-4 right-4';
  }
});

function getTransform() {
  switch (currentPosition.value) {
    case 'top': return 'translateY(-100%)';
    case 'bottom': return 'translateY(100%)';
    case 'left': return 'translateX(-100%)';
    case 'right': return 'translateX(100%)';
  }
}

function toggleDebugPanel() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    updateDebugInfo();
  }
  const cookieValue = {
    value: isOpen.value,
  };

  console.log('cookieValue--->', cookieValue);

  useCookie('debugPanelOpen', cookieValue);

}

function updateDebugInfo() {
  if (performance && performance.memory) {
    const usage = Math.round(performance.memory.usedJSHeapSize / (1024 * 1024));
    const totalMemory = Math.round(performance.memory.totalJSHeapSize / (1024 * 1024));

    const percentage = (usage / totalMemory) * 100;
    const color = percentage < 70 ? 'green' : percentage < 80 ? 'yellow' : 'red';
    memoryUsage.value = `<span style="color: ${color}">${usage} MB of ${totalMemory} MB / ${percentage.toFixed(0)}%</span>`;
  } else {
    memoryUsage.value = 'Not available';
  }

  const cookieString = document.cookie;
  if (cookieString) {
    cookieStatus.value = 'Enabled';
    cookies.value = cookieString.split(';').map(cookie => {
      const [name, value] = cookie.trim().split('=');
      return { name, value };

    });


  } else {
    cookieStatus.value = 'No cookies found';
    cookies.value = [];
  }
}

// Resizing functionality
let isDragging = false;
let startX, startWidth;


function resize(e) {
  if (!isDragging) return;
  const diff = currentPosition.value === 'left' ? e.clientX - startX : startX - e.clientX;
  const newWidth = Math.max(200, Math.min(startWidth + diff, window.innerWidth - 100));
  panelWidth.value = newWidth;
}

function stopResize() {
  isDragging = false;
  document.removeEventListener('mousemove', resize);
  document.removeEventListener('mouseup', stopResize);

  // Snap to nearest percentage
  const percentages = [25, 50, 75];
  const currentPercentage = (panelWidth.value / window.innerWidth) * 100;
  const closestPercentage = percentages.reduce((prev, curr) =>
      Math.abs(curr - currentPercentage) < Math.abs(prev - currentPercentage) ? curr : prev
  );
  selectedWidth.value = closestPercentage;
  updatePanelWidth();
}

function updatePanelWidth() {
  panelWidth.value = (window.innerWidth * selectedWidth.value) / 100;
}

// Watch for position changes
watch(currentPosition, (newPosition) => {
  if (['top', 'bottom'].includes(newPosition)) {
    panelWidth.value = window.innerWidth;
  } else {
    updatePanelWidth(); // Use the selected width percentage
  }
});


let rootStore = useRootStore();


// Lifecycle hooks
onMounted(() => {

  isOpen.value  = rootStore.debugPanelOpen;

  console.log('isOpen.value--->', isOpen.value);

  updateDebugInfo();
  const intervalId = setInterval(updateDebugInfo, 1000);
  onUnmounted(() => clearInterval(intervalId));
});
</script>

